import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { ui, SECTIONS } from "../lib/ui";
import TopicPicker from "../components/TopicPicker";

const MATCH = ["Yes", "No", "Not sure"];
const today = new Date().toISOString().slice(0, 10);

const emptyOral = { reported: false, topic: "", topic_number: null, is_other: false, prediction_match: "" };
const emptyWritten = { reported: false, prompt: "", topic_number: null, is_other: false, prediction_match: "" };

function Rating({ value, onChange, testidPrefix }) {
  return (
    <div className="flex gap-2">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(value === n ? null : n)}
          className={`w-11 h-11 rounded-lg border-2 font-medium transition-colors ${
            value >= n && value != null
              ? "bg-[#8B1A1A] text-[#F5EAE0] border-[#8B1A1A]"
              : "border-[#E8DCC8] text-[#8B1A1A] hover:border-[#8B1A1A]"
          }`}
          data-testid={`${testidPrefix}-${n}`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function MatchRadio({ value, onChange, name }) {
  return (
    <div className="flex flex-wrap gap-2">
      {MATCH.map((m) => (
        <label
          key={m}
          className={`cursor-pointer text-sm px-4 py-2 rounded-full border transition-colors ${
            value === m ? "bg-[#8B1A1A] text-[#F5EAE0] border-[#8B1A1A]" : "bg-[#FFFDF9] text-[#671813] border-[#E8DCC8] hover:border-[#8B1A1A]"
          }`}
        >
          <input type="radio" name={name} value={m} checked={value === m} onChange={() => onChange(m)} className="sr-only" />
          {m}
        </label>
      ))}
    </div>
  );
}

export default function Submit() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cities, setCities] = useState([]);

  const [form, setForm] = useState({
    exam_date: "",
    city: "",
    country: "Canada",
    centre_name: "",
    sections: { eo: false, ee: false, co: false, ce: false },
      eo: { section_a: { ...emptyOral }, section_b: { ...emptyOral }, followup: "", examiner_rating: null, examiner_comment: "" },
      ee: { section_a: { ...emptyWritten }, section_b: { ...emptyWritten } },
    co: { matched_count: "", source: "" },
    ce: { matched_count: "", source: "" },
    difficulty: null,
    note: "",
    email: "",
    confirmed: false,
    website: "",
  });

  const set = (path, value) => {
    setForm((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) ref = ref[keys[i]];
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, []);

  const anySection = Object.values(form.sections).some(Boolean);

  const validateStep1 = () => {
    if (!form.exam_date) return "Please choose your exam date.";
    if (form.exam_date > today) return "Exam date cannot be in the future.";
    if (!form.city.trim()) return "Please enter the test centre city.";
    if (!anySection) return "Please select at least one section to report.";
    return null;
  };

  const next = () => {
    if (step === 1) {
      const err = validateStep1();
      if (err) return toast.error(err);
    }
    setStep((s) => Math.min(3, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const buildPayload = () => {
    const s = form.sections;
    const payload = {
      exam_date: form.exam_date,
      city: form.city.trim(),
      country: form.country,
      centre_name: form.centre_name,
      sections_reported: Object.keys(s).filter((k) => s[k]),
      difficulty: form.difficulty,
      note: form.note,
      email: form.email,
      confirmed: form.confirmed,
      website: form.website,
      eo: null, ee: null, co: null, ce: null,
    };
    if (s.eo) payload.eo = {
      section_a: form.eo.section_a,
      section_b: form.eo.section_b,
      followup: form.eo.followup,
      examiner_rating: form.eo.examiner_rating,
      examiner_comment: form.eo.examiner_comment,
    };
    if (s.ee) payload.ee = { section_a: form.ee.section_a, section_b: form.ee.section_b };
    if (s.co) payload.co = { matched_count: form.co.matched_count === "" ? null : Number(form.co.matched_count), source: form.co.source };
    if (s.ce) payload.ce = { matched_count: form.ce.matched_count === "" ? null : Number(form.ce.matched_count), source: form.ce.source };
    return payload;
  };

  const submit = async () => {
    if (!form.confirmed) return toast.error("Please confirm this is your own real exam experience.");
    setSubmitting(true);
    try {
      await api.post("/reports", buildPayload());
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 sm:px-8 py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#F3E5D8] flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 size={32} className="text-[#8B1A1A]" />
        </div>
        <h1 className="font-serif text-3xl font-bold text-[#8B1A1A]">Thank you</h1>
        <p className="mt-4 text-[#210201] leading-relaxed" data-testid="submit-thankyou-message">
          Your report is in review and will appear in the archive once approved.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a href="https://elitetefprep.com" className={ui.btnPrimary} data-testid="submit-back-to-shop-btn">Back to elitetefprep.com</a>
          <button onClick={() => navigate("/archive")} className={ui.btnSecondary}>Browse the archive</button>
        </div>
      </div>
    );
  }

  const oralFields = (key, label) => (
    <div className={ui.card + " p-5 space-y-4"}>
      <label className="flex items-center gap-2.5 cursor-pointer font-semibold text-[#671813]">
        <input type="checkbox" checked={form.eo[key].reported} onChange={(e) => set(`eo.${key}.reported`, e.target.checked)} className="w-5 h-5 accent-[#8B1A1A]" data-testid={`eo-${key}-check`} />
        Expression orale · {label}
      </label>
      {form.eo[key].reported && (
        <div className="space-y-4 pl-1">
          <div>
            <label className={ui.label}>What was your topic / task?</label>
            <TopicPicker
              section={key === "section_a" ? "eo_a" : "eo_b"}
              value={{ number: form.eo[key].topic_number, text: form.eo[key].topic, is_other: form.eo[key].is_other }}
              onChange={(sel) => { set(`eo.${key}.topic`, sel.text); set(`eo.${key}.topic_number`, sel.number); set(`eo.${key}.is_other`, sel.is_other); }}
              testid={`eo-${key}-topic`}
            />
          </div>
          <div>
            <label className={ui.label}>Was this topic on a prediction plan you studied?</label>
            <MatchRadio value={form.eo[key].prediction_match} onChange={(v) => set(`eo.${key}.prediction_match`, v)} name={`eo-${key}-match`} />
          </div>
        </div>
      )}
    </div>
  );

  const writtenFields = (key, label, helper) => (
    <div className={ui.card + " p-5 space-y-4"}>
      <label className="flex items-center gap-2.5 cursor-pointer font-semibold text-[#671813]">
        <input type="checkbox" checked={form.ee[key].reported} onChange={(e) => set(`ee.${key}.reported`, e.target.checked)} className="w-5 h-5 accent-[#8B1A1A]" data-testid={`ee-${key}-check`} />
        Expression écrite · {label}
      </label>
      <p className="text-xs text-[#5A4441] -mt-2 pl-8">{helper}</p>
      {form.ee[key].reported && (
        <div className="space-y-4 pl-1">
          <div>
            <label className={ui.label}>What was the prompt?</label>
            <TopicPicker
              section={key === "section_a" ? "ee_a" : "ee_b"}
              value={{ number: form.ee[key].topic_number, text: form.ee[key].prompt, is_other: form.ee[key].is_other }}
              onChange={(sel) => { set(`ee.${key}.prompt`, sel.text); set(`ee.${key}.topic_number`, sel.number); set(`ee.${key}.is_other`, sel.is_other); }}
              testid={`ee-${key}-prompt`}
            />
          </div>
          <div>
            <label className={ui.label}>Was this prompt on a prediction plan you studied?</label>
            <MatchRadio value={form.ee[key].prediction_match} onChange={(v) => set(`ee.${key}.prediction_match`, v)} name={`ee-${key}-match`} />
          </div>
        </div>
      )}
    </div>
  );

  const comprehension = (key, label) => (
    <div className={ui.card + " p-5 space-y-4"} data-testid={`${key}-block`}>
      <h3 className="font-semibold text-[#671813]">{label}</h3>
      <div>
        <label className={ui.label}>How many questions came from the prediction plan you studied? (0-40)</label>
        <input type="number" min={0} max={40} value={form[key].matched_count} onChange={(e) => set(`${key}.matched_count`, e.target.value)} className={ui.input} data-testid={`${key}-matched`} />
      </div>
      <div>
        <label className={ui.label}>Which prediction plan did you use? <span className="font-normal text-[#5A4441]">(optional)</span></label>
        <input value={form[key].source} onChange={(e) => set(`${key}.source`, e.target.value)} className={ui.input} data-testid={`${key}-source`} />
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-12">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#8B1A1A] leading-tight">Report your exam</h1>
      <p className="mt-2 text-[#5A4441]">No login needed. You can write in English or French.</p>

      {/* progress */}
      <div className="mt-8 mb-8">
        <div className="flex items-center justify-between mb-2 text-xs uppercase tracking-wider text-[#5A4441]">
          <span>Step {step} of 3</span>
          <span>{step === 1 ? "Exam details" : step === 2 ? "Sections" : "Final"}</span>
        </div>
        <div className="h-2 rounded-full bg-[#E8DCC8] overflow-hidden">
          <div className="h-full bg-[#8B1A1A] transition-all duration-300" style={{ width: `${(step / 3) * 100}%` }} />
        </div>
      </div>

      {/* honeypot */}
      <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      {step === 1 && (
        <div className="space-y-5 fade-up" data-testid="submit-form-step-1">
          <div>
            <label className={ui.label}>Exam date</label>
            <input type="date" max={today} value={form.exam_date} onChange={(e) => set("exam_date", e.target.value)} className={ui.input} data-testid="exam-date-input" />
          </div>
          <div>
            <label className={ui.label}>Test centre city</label>
            <input list="city-list" value={form.city} onChange={(e) => set("city", e.target.value)} className={ui.input} placeholder="e.g. Montréal" data-testid="city-input" />
            <datalist id="city-list">{cities.map((c) => <option key={c} value={c} />)}</datalist>
          </div>
          <div>
            <label className={ui.label}>Test centre country</label>
            <select value={form.country} onChange={(e) => set("country", e.target.value)} className={ui.input} data-testid="country-select">
              {["Canada", "France", "Morocco", "Algeria", "Tunisia", "Cameroon", "Senegal", "United States", "United Kingdom", "Other"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className={ui.label}>Test centre name <span className="font-normal text-[#5A4441]">(optional)</span></label>
            <input value={form.centre_name} onChange={(e) => set("centre_name", e.target.value)} className={ui.input} data-testid="centre-name-input" />
          </div>
          <div>
            <label className={ui.label}>Which sections are you reporting?</label>
            <div className="grid gap-2 sm:grid-cols-2">
              {SECTIONS.map((s) => (
                <label key={s.code} className={`flex items-center gap-3 cursor-pointer rounded-xl border p-3 transition-colors ${form.sections[s.code] ? "border-[#8B1A1A] bg-[#F3E5D8]" : "border-[#E8DCC8] bg-[#FFFDF9] hover:border-[#8B1A1A]/50"}`}>
                  <input type="checkbox" checked={form.sections[s.code]} onChange={(e) => set(`sections.${s.code}`, e.target.checked)} className="w-5 h-5 accent-[#8B1A1A]" data-testid={`section-check-${s.code}`} />
                  <span><span className="font-medium text-[#210201]">{s.label}</span> <span className="text-xs text-[#5A4441]">({s.sub})</span></span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6 fade-up" data-testid="submit-form-step-2">
          {!anySection && <p className="text-[#5A4441]">Go back and pick at least one section.</p>}

          {form.sections.eo && (
            <div className="space-y-4">
              <h2 className="font-serif text-xl font-bold text-[#8B1A1A]">Expression orale</h2>
              {oralFields("section_a", "Section A")}
              {oralFields("section_b", "Section B")}
              <div className={ui.card + " p-5 space-y-4"}>
                <div>
                  <label className={ui.label}>Follow-up questions or objections from the examiner? <span className="font-normal text-[#5A4441]">(optional)</span></label>
                  <textarea rows={2} value={form.eo.followup} onChange={(e) => set("eo.followup", e.target.value)} className={ui.input} data-testid="eo-followup" />
                </div>
                <div>
                  <label className={ui.label}>How was the examiner? <span className="font-normal text-[#5A4441]">(optional)</span></label>
                  <Rating value={form.eo.examiner_rating} onChange={(v) => set("eo.examiner_rating", v)} testidPrefix="examiner-rating" />
                  <p className="text-xs text-[#5A4441] mt-2">Please don't include the examiner's name.</p>
                  <textarea rows={2} value={form.eo.examiner_comment} onChange={(e) => set("eo.examiner_comment", e.target.value)} className={ui.input + " mt-2"} placeholder="Short comment" data-testid="eo-examiner-comment" />
                </div>
              </div>
            </div>
          )}

          {form.sections.ee && (
            <div className="space-y-4">
              <h2 className="font-serif text-xl font-bold text-[#8B1A1A]">Expression écrite</h2>
              {writtenFields("section_a", "Section A", "Newspaper article continuation / fait divers")}
              {writtenFields("section_b", "Section B", "Opinion essay")}
            </div>
          )}

          {form.sections.co && (
            <div className="space-y-2">
              <h2 className="font-serif text-xl font-bold text-[#8B1A1A]">Compréhension orale</h2>
              {comprehension("co", "Listening comprehension")}
            </div>
          )}

          {form.sections.ce && (
            <div className="space-y-2">
              <h2 className="font-serif text-xl font-bold text-[#8B1A1A]">Compréhension écrite</h2>
              {comprehension("ce", "Reading comprehension")}
            </div>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5 fade-up" data-testid="submit-form-step-3">
          <div>
            <label className={ui.label}>Overall difficulty</label>
            <Rating value={form.difficulty} onChange={(v) => set("difficulty", v)} testidPrefix="difficulty" />
          </div>
          <div>
            <label className={ui.label}>Anything else you want to share? <span className="font-normal text-[#5A4441]">(optional)</span></label>
            <textarea rows={4} value={form.note} onChange={(e) => set("note", e.target.value)} className={ui.input} data-testid="note-input" />
          </div>
          <div>
            <label className={ui.label}>Your email <span className="font-normal text-[#5A4441]">(optional, never shown publicly)</span></label>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={ui.input} placeholder="you@example.com" data-testid="email-input" />
          </div>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.confirmed} onChange={(e) => set("confirmed", e.target.checked)} className="w-5 h-5 accent-[#8B1A1A] mt-0.5" data-testid="confirm-check" />
            <span className="text-sm text-[#210201]">I confirm this is my own real exam experience.</span>
          </label>
        </div>
      )}

      {/* nav buttons */}
      <div className="mt-10 flex items-center justify-between gap-3">
        {step > 1 ? (
          <button onClick={prev} className={ui.btnSecondary} data-testid="submit-form-prev-btn"><ArrowLeft size={16} /> Back</button>
        ) : <span />}
        {step < 3 ? (
          <button onClick={next} className={ui.btnPrimary + " flex-1 sm:flex-none"} data-testid="submit-form-next-btn">Continue <ArrowRight size={16} /></button>
        ) : (
          <button onClick={submit} disabled={submitting} className={ui.btnPrimary + " flex-1 sm:flex-none"} data-testid="submit-form-submit-btn">
            {submitting ? "Submitting…" : "Submit report"}
          </button>
        )}
      </div>
    </div>
  );
}
